import type { Result } from 'neverthrow';

import { apiRequest, type ApiError } from './client';

/**
 * Frontend mirror of the backend `SyncJobSchema` (ADR 0019). The
 * progress modal polls `GET /sync-jobs/{id}` and renders this shape.
 */
export type SyncJobStatus = 'queued' | 'running' | 'completed' | 'failed';

export interface SyncJob {
  readonly job_id: string;
  readonly tenant_id: string;
  readonly connection_id: string;
  readonly kind: 'manual' | 'resync' | 'reconcile';
  readonly status: SyncJobStatus;
  readonly enqueued_count: number;
  // Optional — older job rows pre-dating PR 72 may lack it; new rows
  // start at 0 and tick up as the order-sync-worker drains the queue.
  readonly synced_count?: number;
  // Marker written by the sync-jobs-worker when enqueueing finishes;
  // until set, the modal shows "Enqueueing…" alongside the queued
  // count to make the producer / consumer split legible.
  readonly enqueue_completed_at?: string;
  readonly since?: string;
  readonly until?: string;
  readonly truncated?: boolean;
  readonly error?: string;
  readonly created_at: string;
  readonly started_at?: string;
  readonly completed_at?: string;
}

export function getSyncJob(jobId: string): Promise<Result<SyncJob, ApiError>> {
  return apiRequest<SyncJob>({
    method: 'GET',
    path: `/sync-jobs/${encodeURIComponent(jobId)}`,
  });
}

export const TERMINAL_SYNC_JOB_STATUSES: readonly SyncJobStatus[] = ['completed', 'failed'];

export function isTerminalSyncJobStatus(status: SyncJobStatus): boolean {
  return TERMINAL_SYNC_JOB_STATUSES.includes(status);
}

export const SYNC_JOB_QUERY_KEY = (jobId: string) => ['sync-job', jobId] as const;
